<template>
    <span class="dynalive-import">
        <sui-button @click="openModal">
            Import Dynalive
        </sui-button>

        <sui-modal v-model="isModalOpen">
            <sui-modal-header>
                Import Dynalive
            </sui-modal-header>
            <sui-modal-content>
                <sui-segment v-if="status">
                    <div><strong>Status:</strong> {{ status.status }}</div>
                    <div><strong>Date de début:</strong> {{ startedAt }}</div>
                    <div v-if="status.finishedAt"><strong>Date de fin:</strong> {{ finishedAt }}</div>
                    <div><strong>Progression:</strong> {{ progress }}%</div>
                    <br>
                    <sui-button @click="getLogs">Télécharger les logs</sui-button>
                </sui-segment>
                <sui-segment :disabled="!canStart">
                    <sui-input 
                        placeholder="Entrez l'ID Dynalive ici..." 
                        v-model="dynaliveId" 
                        :error="inputError" 
                    />
                    <div style="padding-top: 10px;">
                        <sui-checkbox label="Import complet" toggle v-model="fullMode" />
                    </div>
                    <br>
                    <div>
                        <div v-if="loading">Demande d'import en cours...</div>
                        <div v-if="reqError">
                            <div>Une erreur est survenue!</div>
                            <pre>{{ reqError.message }}</pre>
                        </div>
                        <div v-if="reqDone">
                            L'import a démarré
                        </div>
                    </div>
                    <br>
                    <sui-button primary :disabled="!canStart" @click="startImport">
                        Lancer
                    </sui-button>
                </sui-segment>
            </sui-modal-content>
            <sui-modal-actions>
                <sui-button @click="closeModal">
                    Fermer
                </sui-button>
            </sui-modal-actions>
        </sui-modal>
    </span>
</template>

<script>
import { format } from 'date-fns';

const BASE_URL = `${process.env.VUE_APP_API_URL}/monitoring-bff/v1/dynalive`;

export default {
    data: () => ({
        isModalOpen: false,
        dynaliveId: '',
        inputError: false,
        fullMode: false,
        loading: false,
        reqError: null,
        reqDone: false,
        status: null,
        statusInterval: null,
    }),

    computed: {
        customerId() {
            return this.$store.getters.activeCustomer.id;
        },

        progress() {
            return this.status ? Math.round(this.status.progress || 0) : 0;
        },

        startedAt() {
            if (this.status)
                return format(new Date(this.status.startedAt), 'dd/MM/yyyy HH:mm:ss');
            return null;
        },

        finishedAt() {
            if (this.status)
                return format(new Date(this.status.finishedAt), 'dd/MM/yyyy HH:mm:ss');
            return null;
        },

        canStart() {
            return !(this.status && ['active', 'waiting'].includes(this.status.status));
        },
    },

    watch: {
        dynaliveId: {
            handler(v) {
                this.inputError = v.trim().length === 0;
            },
        },
    },

    async created() {
        await this.getStatus();
        if (this.status && ['active', 'waiting'].includes(this.status.status)) {
            this.statusInterval = setInterval(() => {
                this.getStatus();
            }, 10000);
        }
    },

    beforeDestroy() {
        if (this.statusInterval) {
            clearInterval(this.statusInterval);
            this.statusInterval = null;
        }
    },

    methods: {
        openModal() {
            this.isModalOpen = true;
        },

        closeModal() {
            this.isModalOpen = false;
            this.reqError = null;
            this.reqDone = false;
        },

        async getStatus() {
            try {
                const response = await fetch(`${BASE_URL}/${this.customerId}/medias-import/status`);
                if (!response.ok) {
                    throw new Error('Unable to get status');
                }
                const data = await response.json();
                this.status = data;
                this.dynaliveId = this.status.dynaliveId.toString();

                if (['active', 'waiting'].includes(data.status) === false && this.statusInterval) {
                    clearInterval(this.statusInterval);
                    this.statusInterval = null;
                }

                return data;
            } catch (err) {
                // Silent error: no previous import
            }
        },

        async getLogs() {
            const response = await fetch(`${BASE_URL}/${this.customerId}/medias-import/status-logs`);
            if (!response.ok) {
                throw new Error('Unable to get logs');
            }
            const blob = await response.blob();

            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'dynalive-import.json';
            document.body.appendChild(link);
            link.click();
            link.remove();
        },

        async startImport() {
            this.reqDone = false;
            if (!this.dynaliveId) {
                this.inputError = true;
            } else {
                this.reqError = null;
                this.loading = true;
                try {
                    const response = await fetch(`${BASE_URL}/${this.customerId}/medias-import`, {
                        method: 'POST',
                        headers: {
                            'content-type': 'application/json'
                        },
                        body: JSON.stringify({
                            dynaliveId: this.dynaliveId,
                            fullMode: this.fullMode,
                        })
                    });

                    const responseContent = await response.text();
                    if (!responseContent) {
                        throw new Error('Aucun nouvel élément à importer');
                    }

                    const data = JSON.parse(responseContent);

                    if (!response.ok) {
                        throw new Error(await response.text() || response.statusText);
                    }

                    this.status = data;
                    this.dynaliveId = this.status.dynaliveId.toString();
                    this.reqDone = true;

                    this.statusInterval = setInterval(() => {
                        this.getStatus();
                    }, 10000);
                } catch (err) {
                    this.reqError = err;
                } finally {
                    this.loading = false;
                }
            }
        },
    }
}
</script>